import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import Pagination from "@material-ui/lab/Pagination";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import BusinessIcon from "@material-ui/icons/Business";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import IndustryIcon from "../../assets/images/svg/industry-solid-full-brown.svg";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import { useDispatch } from "react-redux";
import { OAuth as OAuthActions } from "../../js/actions";
import AddOemDialog from "../AddOem";

const ROWS_OPTIONS = [5, 10, 20];

const COLORS = {
  navyDark: "#1e40af",
  blue800: "#1e40af",
  blue700: "#1d4ed8",
  blue100: "#dbeafe",
  blue50: "#eff6ff",
  emerald700: "#047857",
  emerald50: "#ecfdf5",
  emerald200: "#a7f3d0",
  slate500: "#64748b",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  amber500: "#f59e0b",
  red500: "#ef4444",
  border: "#e2e8f0",
  muted: "#f1f5f9",
  mutedForeground: "#64748b",
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    padding: "24px",
    backgroundColor: "#F8FAFC",
    minHeight: "calc(100vh - 127px)",
  },

  main: {
    // maxWidth: 1400,
    margin: "0 auto",
  },

  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },

  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },

  pageIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
  },

  addButton: {
    textTransform: "none",
    backgroundColor: COLORS.blue700,
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: COLORS.blue800,
    },
    "& .MuiButton-label": {
      fontSize: "13px",
    },
  },

  statCard: {
    padding: "20px 20px",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "all 0.15s",
  },

  statLabel: {
    color: COLORS.mutedForeground,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontSize: 12,
  },

  statValue: {
    marginTop: 4,
    fontWeight: 600,
  },

  statIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  tableCard: {
    borderRadius: 16,
    overflow: "hidden",
  },

  toolbar: {
    padding: "0px 0px 8px 24px",
    borderBottom: `1px solid ${COLORS.border}`,
  },

  searchField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiOutlinedInput-root": {
      height: 50,
      width: 250,
      backgroundColor: COLORS.muted,
      borderRadius: 8,
    },
  },

  searchIcon: {
    fontSize: 18,
    color: COLORS.mutedForeground,
  },

  table: {
    tableLayout: "fixed",
  },

  tableHeadRow: {
    backgroundColor: "rgba(241,245,249,0.8)",
  },

  tableHeadCell: {
    fontSize: 12,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
  },

  makeChip: {
    backgroundColor: COLORS.blue50,
    color: COLORS.blue700,
    border: `1px solid ${COLORS.blue100}`,
    fontSize: "0.85rem",
  },

  normalRow: {
    backgroundColor: "#fff",
    "& .MuiTypography-root": {
      color: "#000",
      fontSize: "0.85rem",
    },
    "&:hover": {
      backgroundColor: "rgba(241,245,249,0.5)",
    },
  },

  emptyCell: {
    padding: "48px 0",
    color: COLORS.mutedForeground,
  },

  footer: {
    padding: "12px 24px",
    borderTop: `1px solid ${COLORS.border}`,
    backgroundColor: "rgba(241,245,249,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    display: "flex",
    alignItems: "center",
  },

  footerText: {
    color: COLORS.mutedForeground,
    marginRight: 8,
  },

  rowsSelect: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiSelect-select": {
      padding: "5px 25px 5px 10px",
    },
  },

  footerRight: {
    display: "flex",
    alignItems: "center",
  },

  pageText: {
    color: COLORS.mutedForeground,
    marginRight: 12,
  },
}));

const ManageOem = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [oems, setOems] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("All");
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const RefreshDataSet = async () => {
    await dispatch(OAuthActions.getAllOems()).then(function (oemData) {
      if (!oemData) {
        setOems([]);
        return;
      }

      const newDataSet = oemData.map((item) => {
        let date = new Date(item.addedOn);
        let year = date.getFullYear();
        let month = date.getMonth() + 1;
        let day = date.getDate();

        if (day < 10) day = "0" + day;
        if (month < 10) month = "0" + month;

        return {
          oemName: item.oemName || "",
          parentOemName:
            item.parentOemName == null ? "--" : item.parentOemName,
          addedOn: month + "/" + day + "/" + year,
        };
      });

      setOems(newDataSet);
    });
  };

  useEffect(() => {
    RefreshDataSet();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return oems.filter((o) => {
      const matchesSearch =
        (o.oemName || "").toLowerCase().includes(q) ||
        (o.parentOemName || "").toLowerCase().includes(q) ||
        (o.addedOn || "").toLowerCase().includes(q);

      const isParent = o.parentOemName === "--" || !o.parentOemName;
      const matchesFilter =
        statusFilter === "All" ||
        (statusFilter === "Parent" && isParent) ||
        (statusFilter === "Child" && !isParent);

      return matchesSearch && matchesFilter;
    });
  }, [search, oems, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const parentCount = oems.filter(
    (o) => o.parentOemName === "--" || !o.parentOemName
  ).length;
  const childCount = oems.filter(
    (o) => o.parentOemName !== "--" && o.parentOemName
  ).length;

  const statCards = [
    {
      label: "Total OEMs",
      filter: "All",
      value: oems.length,
      icon: <AllInclusiveIcon style={{ fontSize: 22 }} />,
      color: COLORS.blue700,
      bg: COLORS.blue50,
    },
    {
      label: "Parent OEMs",
      filter: "Parent",
      value: parentCount,
      icon: <BusinessIcon style={{ fontSize: 22 }} />,
      color: COLORS.emerald700,
      bg: COLORS.emerald50,
    },
    {
      label: "Child OEMs",
      filter: "Child",
      value: childCount,
      icon: <AccountTreeIcon style={{ fontSize: 22 }} />,
      color: COLORS.slate500,
      bg: COLORS.slate100,
    },
  ];

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleAddOem = () => {
    setAddDialogOpen(true);
  };

  return (
    <Box className={classes.root}>
      <Box component="main" className={classes.main}>
        <Box className={classes.pageHeader}>
          <Box className={classes.pageHeaderLeft}>
            <Box className={classes.pageIconBox}>
              <img src={IndustryIcon} alt="industry" style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />
            </Box>

            <Typography variant="h5">
              <strong style={{ fontSize: "1.4rem" }}>Manage OEM</strong>
            </Typography>
          </Box>

          <Box className={classes.headerActions}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOem}
              className={classes.addButton}
              style={{ fontSize: 20 }}
            >
              Add OEM
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          {statCards.map((s) => {
            const isActive = statusFilter === s.filter;

            return (
              <Grid item xs={12} sm={4} key={s.label}>
                <Paper
                  variant="outlined"
                  onClick={() => {
                    setStatusFilter(s.filter);
                    setPage(1);
                  }}
                  className={classes.statCard}
                  style={{
                    borderColor: isActive ? s.color : COLORS.border,
                    boxShadow: isActive
                      ? "0px 2px 8px rgba(0,0,0,0.15)"
                      : "none",
                  }}
                >
                  <Box>
                    <Typography className={classes.statLabel}>
                      {s.label}
                    </Typography>

                    <Typography
                      variant="h3"
                      className={classes.statValue}
                      style={{ color: s.color }}
                    >
                      {s.value}
                    </Typography>
                  </Box>

                  <Box
                    className={classes.statIconBox}
                    style={{
                      backgroundColor: s.bg,
                      color: s.color,
                    }}
                  >
                    {s.icon}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Paper variant="outlined" className={classes.tableCard}>
          <Box className={classes.toolbar}>
            <TextField
              size="small"
              variant="outlined"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search OEMs..."
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  {[
                    { label: "OEM Name", width: "30%" },
                    { label: "Parent OEM Name", width: "35%" },
                    { label: "Added On (mm/dd/yyyy)", width: "35%" },
                  ].map(({ label, width }) => (
                    <TableCell
                      key={label}
                      className={classes.tableHeadCell}
                      style={{ width }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((oem, index) => (
                  <TableRow key={index} className={classes.normalRow}>
                    <TableCell>
                      <Chip
                        label={oem.oemName}
                        size="small"
                        className={classes.makeChip}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" noWrap display="block">
                        {oem.parentOemName}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" noWrap display="block">
                        {oem.addedOn}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {paginated.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      className={classes.emptyCell}
                    >
                      No OEMs match your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.footer}>
            <Box className={classes.footerLeft}>
              <Typography variant="body2" className={classes.footerText}>
                Rows per page:
              </Typography>

              <Select
                style={{ borderRadius: 8 }}
                size="small"
                variant="outlined"
                value={rowsPerPage}
                onChange={handleRowsPerPage}
                className={classes.rowsSelect}
              >
                {ROWS_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box className={classes.footerRight}>
              <Typography variant="body2" className={classes.pageText}>
                Page {page} of {totalPages}
              </Typography>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                shape="circular"
                size="small"
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      <AddOemDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={RefreshDataSet}
      />
    </Box>
  );
};

export default ManageOem;
